<template>
    <div id="VolunteerView">
        <OpportunityPageLayout
            v-if="isVisible || preview"
            back-label="Späť na dobrovoľníkov"
            :minified="minified"
            :preview="preview"
            reverse-bottom-style
            :to="{
                name: 'dobrovolnictvo-dobrovolnici',
                query: route.query,
                hash: `#r-id-${route.params.id}`
            }"
        >
            <ProfileViewLayoutTop
                :full-name="fullName"
                :heading-tag="minified || preview ? undefined : 'h1'"
                :interests="profile.volunteerProfile?.interests"
                :minified="minified"
                :preview="preview"
                :profile="profile"
                :profile-view-type="profileViewType"
                search-tag-query-param="interests"
                search-tag-route-name="dobrovolnictvo-dobrovolnici"
            />
            <template #middle>
                <OpportunityPageButtons
                    v-if="!minified"
                    :id="profile.id"
                    fixed-on-mobile
                    :is-flexbox="isProfileOwner"
                    :preview="preview"
                    :title="fullName"
                    type="volunteer"
                >
                    <AskForVolunteerButton
                        :preview="preview"
                        :profile-owner="isProfileOwner"
                        :volunteer="profile"
                        :volunteer-first-name="profile.firstName"
                        :volunteer-id="profile.id"
                        :volunteer-last-name="profile.lastName"
                        :volunteer-slug="profile.slug || profile.id"
                    />
                </OpportunityPageButtons>
            </template>

            <template #left>
                <ProfileLongStory :minified="minified" :preview="preview" :profile="profile" />
            </template>
            <template #right>
                <ProfileViewLayoutRight :minified="minified" :preview="preview" :profile="profile">
                    <PreferenceGroup v-if="!profile.dummy" :preferences="profile.volunteerProfile" />

                    <template #footer>
                        <CCardBox v-if="updatedAtDate">
                            <CFormGroup label="Naposledy aktualizované">
                                {{ updatedAtDate }}
                            </CFormGroup>
                        </CCardBox>
                    </template>
                </ProfileViewLayoutRight>
            </template>
        </OpportunityPageLayout>

        <div v-else>
            <NotFound title="Ľutujeme">
                <p>
                    Tvoj dobrovoľnícky profil zatiaľ nie je aktívny. Viac informácií o tom, prečo sa stať dobrovoľníkom
                    a ako na to, nájdeš na podstránke
                    <NuxtLink
                        class="link"
                        :to="{
                            name: 'chcem-byt-dobrovolnik'
                        }"
                    >
                        Chcem byť dobrovoľníkom</NuxtLink
                    >.
                </p>
                <p>
                    Alebo si hneď teraz vyplň
                    <NuxtLink
                        class="link"
                        :to="{
                            name: 'profile-volunteer-preferences'
                        }"
                    >
                        dobrovoľnícky profil</NuxtLink
                    >.
                </p>
            </NotFound>
        </div>
    </div>
</template>

<script setup lang="ts">
import { isEmpty } from 'lodash-es'
import { formatDate, getFullName } from '#shared/utils'
import { EntityType } from '#shared/types/EntityType'
import { VisibilityType } from '#shared/types/visibility'
import type { UserProfile } from '#shared/types/User'

interface Props {
    profile: UserProfile
    preview?: boolean
    minified?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    preview: false,
    minified: false
})

const route = useRoute()
const store = useApplicationStore()
const profileViewType = EntityType.Volunteer

const isVisible = computed(() => {
    return Boolean(
        props.profile?.volunteerProfile &&
            (store.userId === props.profile?.id ||
                props.profile?.volunteerProfile?.visibility !== VisibilityType.hidden)
    )
})

const isProfileOwner = computed(() => {
    return store.userId === props.profile?.id
})

const fullName = computed(() => {
    return getFullName(props.profile)
})

const updatedAtDate = computed(() => {
    if (!props.profile?.volunteerProfile?.updatedAt) {
        return ''
    }

    return formatDate(props.profile.volunteerProfile.updatedAt)
})
</script>
